import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Trash2, FileVideo, Check, RefreshCw, WifiOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIDEO_CACHE = "yems-videos-v7";

function fmtMB(bytes) {
  return (bytes / (1024 * 1024)).toFixed(1);
}

/**
 * Dialog showing offline-video cache status with per-video size and a single
 * "Download All" button. Powered by the existing service-worker (SW) video
 * cache (`yems-videos-v7`) — re-querying the cache after each fetch tells us
 * exactly which files are saved locally.
 */
export default function OfflineVideosDialog({ open, onOpenChange, trigger }) {
  const [videos, setVideos] = useState([]); // [{ filename, path, size_mb, cached }]
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/videos`);
      const list = res.data?.videos || [];
      // Check cache status for each
      let cache = null;
      if ("caches" in window) {
        try { cache = await caches.open(VIDEO_CACHE); } catch { cache = null; }
      }
      const enriched = await Promise.all(list.map(async (v) => {
        let cached = false;
        if (cache) {
          const hit = await cache.match(v.path);
          cached = !!hit;
        }
        return { ...v, cached };
      }));
      setVideos(enriched);
    } catch {
      toast.error("Failed to load video list");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const totalMB = videos.reduce((sum, v) => sum + (v.size_mb || 0), 0);
  const cachedMB = videos.filter((v) => v.cached).reduce((sum, v) => sum + (v.size_mb || 0), 0);
  const allCached = videos.length > 0 && videos.every((v) => v.cached);
  const noneCached = videos.length > 0 && videos.every((v) => !v.cached);

  const downloadAll = async () => {
    if (!("caches" in window)) {
      toast.error("Offline cache not supported in this browser");
      return;
    }
    setDownloading(true);
    setProgress({ done: 0, total: videos.length });
    try {
      const cache = await caches.open(VIDEO_CACHE);
      let done = 0;
      let failed = 0;
      for (const v of videos) {
        if (!v.cached) {
          try {
            const r = await fetch(v.path);
            if (r.ok) await cache.put(v.path, r.clone());
            else failed++;
          } catch {
            failed++;
          }
        }
        done++;
        setProgress({ done, total: videos.length });
      }
      if (failed === 0) {
        toast.success(`Downloaded ${videos.length} video${videos.length !== 1 ? "s" : ""} for offline use`);
      } else {
        toast.warning(`Downloaded ${videos.length - failed}/${videos.length} videos`, {
          description: `${failed} failed — check connection and retry.`,
        });
      }
      await refresh();
    } finally {
      setDownloading(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Remove all cached videos? You'll need a connection to redownload them.")) return;
    try {
      await caches.delete(VIDEO_CACHE);
      // Re-create empty cache so SW can populate it again later
      await caches.open(VIDEO_CACHE);
      toast.success("Offline videos cleared");
      await refresh();
    } catch {
      toast.error("Failed to clear cache");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger}
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <WifiOff className="w-5 h-5 text-cyan-400" />
            Offline Videos
          </DialogTitle>
          <DialogDescription className="sr-only">Manage which videos are saved on this device for offline playback.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Summary */}
          <div className="rounded-lg border bg-secondary/20 p-3 grid grid-cols-3 gap-2 text-center" data-testid="offline-videos-summary">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total</div>
              <div className="text-lg font-mono font-bold">{videos.length}</div>
              <div className="text-[10px] text-muted-foreground">{totalMB.toFixed(1)} MB</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Cached</div>
              <div className={`text-lg font-mono font-bold ${allCached ? "text-emerald-400" : noneCached ? "text-muted-foreground" : "text-cyan-400"}`}>
                {videos.filter((v) => v.cached).length}/{videos.length}
              </div>
              <div className="text-[10px] text-muted-foreground">{cachedMB.toFixed(1)} MB</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">To download</div>
              <div className="text-lg font-mono font-bold text-amber-400">
                {(totalMB - cachedMB).toFixed(1)}
              </div>
              <div className="text-[10px] text-muted-foreground">MB</div>
            </div>
          </div>

          {/* Video list */}
          <div className="border rounded-lg overflow-hidden divide-y divide-border max-h-64 overflow-y-auto" data-testid="offline-videos-list">
            {loading && videos.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
            ) : videos.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">No videos available.</div>
            ) : (
              videos.map((v) => (
                <div key={v.filename} className="flex items-center gap-2 px-3 py-2.5">
                  <FileVideo className={`w-4 h-4 shrink-0 ${v.cached ? "text-emerald-400" : "text-muted-foreground"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs truncate">{v.filename}</div>
                    <div className="text-[10px] text-muted-foreground">{v.size_mb} MB</div>
                  </div>
                  {v.cached ? (
                    <Badge variant="outline" className="text-[10px] gap-1 text-emerald-400 border-emerald-500/40">
                      <Check className="w-3 h-3" />Saved
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/40">
                      Not saved
                    </Badge>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Progress bar during download */}
          {downloading && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-[11px] text-muted-foreground">
                <span>Downloading…</span>
                <span className="tabular-nums">{progress.done} / {progress.total}</span>
              </div>
              <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                <div
                  className="bg-cyan-500 h-full transition-all"
                  style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              onClick={downloadAll}
              disabled={downloading || loading || allCached || videos.length === 0}
              className="flex-1 bg-cyan-600 hover:bg-cyan-700"
              data-testid="offline-videos-download"
            >
              {downloading ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Downloading…</>
              ) : allCached ? (
                <><Check className="w-4 h-4 mr-1.5" />All saved</>
              ) : (
                <><Download className="w-4 h-4 mr-1.5" />Download all ({(totalMB - cachedMB).toFixed(1)} MB)</>
              )}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={refresh}
              disabled={downloading || loading}
              title="Refresh cache status"
              data-testid="offline-videos-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={clearAll}
              disabled={downloading || loading || cachedMB === 0}
              title="Clear all cached videos"
              data-testid="offline-videos-clear"
            >
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
          </div>

          <p className="text-[11px] text-muted-foreground italic">
            Videos load instantly without an internet connection once saved. Cached videos persist until cleared.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
